import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { TEAM, ST } from '../core/entities.js';
import { makeHorse, HORSE } from '../chars/horse.js';
import { surfaceMaterial } from '../world/mat.js';
import { SITES } from '../world/layout.js';

/**
 * ===========================================================================
 *  MOUNTS — the horse, and the only thing in the plan that was not already built
 * ===========================================================================
 *
 * DEV_PLAN Step 7. Steps 5 and 6 were state machines over systems that already
 * existed; this one had nothing to stand on. `ST.MOUNTED` sat unused in the
 * entity enum and `KeyF:'mount'` sat unbound to any behaviour, and that was the
 * whole of it.
 *
 * WHAT A MOUNT IS HERE. The horse is NOT an entity. It is a mesh and a record
 * that follow the player's own entity, which means mounted movement reuses
 * every system the player already goes through — terrain clamping, the camera
 * rig, combat, the AI's idea of where the hero is — instead of introducing a
 * second body that all of them would need to learn about. Riding changes two
 * numbers on the player (`mountSpeed`, `mountAccel`) and moves the mesh; it
 * does not fork the pipeline.
 *
 * That choice has a cost and it is the honest one to name: the horse cannot be
 * hit, killed or stolen, because nothing can target what is not an entity.
 * A horse with hit points belongs with the step that needs it.
 *
 * THE HEAVINESS IS THE POINT. A horse is not just faster: `accel` is more than
 * halved, so it takes time to get going and refuses to turn on the spot. The
 * traversal win is real and it is paid for in responsiveness, which is exactly
 * the trade the shipping games make.
 */

export const MOUNT = {
  /** Top speed multiplier. The traversal gate lives or dies on this. */
  speed: 2.05,
  /** ...and the price: a horse accelerates and stops far worse than a man. */
  accel: 0.42,
  /** How close you must be to swing up. */
  range: 4.5,
  /** A floor between mount and dismount, so one key press is one action. */
  cooldown: 0.4,
  /** How fast the horse's heading catches the rider's. Lower is heavier. */
  turnRate: 2.6,

  /* ---- gait --------------------------------------------------------------
   * The horse is ONE MERGED MESH with no skeleton, so the gallop cannot be in
   * the legs. It is carried by the body instead: a two-beat bob with a pitch
   * that leads it and a roll into the turn, which is most of what reads at
   * musou distance anyway — a galloping animal is a rising and falling mass
   * long before it is four legs.
   *
   * Driven by DISTANCE TRAVELLED, not by time. A time-driven cycle keeps
   * galloping while the horse stands still, which is the exact tell that says
   * "this is an animation playing" rather than "this animal is moving". */
  stride: 2.45,      // metres per full cycle
  bob: 0.085,        // vertical travel of the body, metres
  pitch: 0.075,      // radians of nose-up/nose-down across the cycle
  roll: 0.18,        // radians of lean into a turn, at full turn rate

  /* The mounted attack: longer reach and a wider arc than anything on foot,
   * because the whole point of a charge is that you clear a lane through men
   * who cannot reach you back. */
  reach: 3.6,
  arcHalf: 105,
  damage: 62,
  attackCool: 0.55,
};

/**
 * Where horses start — DERIVED FROM THE MAP, not hard-coded.
 *
 * The first three stables were three coordinates I picked, and the Step 7
 * verdict recorded what that produced: two of them sat where no mission ever
 * sends the player, so in practice only one horse in the game was ever
 * reachable. Placing them by hand again would just move the same problem.
 *
 * These are read off `terrain.sites`, the authored layout that the bases, the
 * objectives and the minimap all already come from. A horse stands outside
 * every holding the player has business at — their own fort, the bridge, the
 * posts, the friendly camp — and a stage that adds a site gets a stable with
 * it. The enemy's main fort is skipped: a horse waiting inside the objective
 * is a horse you reach after you no longer need it.
 *
 * `offset` pushes each one toward the middle of the map so it stands OUTSIDE
 * the walls rather than in a courtyard.
 */
const STABLE_OFFSET = 16;
/**
 * Read straight off the imported layout, NOT off `world.terrain.sites`.
 *
 * The first attempt did the latter and spawned no horses at all: this system
 * is ORDER.MISSION (10) and the terrain publishes later, so `w.terrain` is
 * undefined when init() runs and the site list came back empty. Every mount
 * contract check then failed on an empty `list`. `SITES` is an authored
 * constant with no init order to get wrong.
 */
function stablesFrom() {
  const sites = SITES || [];
  const out = [];
  for (const s of sites) {
    if (s.type === 'fort' && s.team === 1) continue;      // not the enemy keep
    const len = Math.hypot(s.x, s.z) || 1;
    const x = s.x - (s.x / len) * STABLE_OFFSET;
    const z = s.z - (s.z / len) * STABLE_OFFSET;
    out.push({ id: s.id, at: s.name || s.id, x, z, yaw: Math.atan2(-s.x, -s.z) });
  }
  return out;
}

/**
 * The ride prompt.
 *
 * A verb the player cannot discover is not a verb. `KeyF` sat in the keymap
 * through every session before this one and did nothing, and nothing on screen
 * would ever have told anyone it was there. This is the smallest thing that
 * fixes that: while a free horse is in range, the key is named.
 *
 * It deliberately does NOT offer to dismount. A permanent "press F" over the
 * hero for as long as he is riding is clutter, and the toast on mounting has
 * already said the word once.
 *
 * Placed at 300u — below the duel's challenge at 210u and above the hero's
 * head. Two HUD collisions this phase were invisible in the CSS and only found
 * in a frame, so this one is photographed by critic_mount_ui alongside a duel
 * prompt rather than reasoned about.
 */
const PROMPT_CSS = [
  '.sb .ride{position:absolute; left:50%; top:calc(var(--u)*300); width:calc(var(--u)*360);',
  '  margin-left:calc(var(--u)*-180); opacity:0; text-align:center; pointer-events:none}',
  '.sb .ride .rk{font-size:calc(var(--u)*10.5); font-weight:700;',
  '  letter-spacing:calc(var(--u)*3.4); text-transform:uppercase; color:rgba(255,226,170,.92);',
  '  text-shadow:0 calc(var(--u)*2) calc(var(--u)*10) rgba(0,0,0,.95)}',
  '.sb .ride .rk b{color:#0e0b07; background:linear-gradient(180deg,#ffe6ac,#d9a44a);',
  '  padding:0 calc(var(--u)*6); margin:0 calc(var(--u)*3)}',
].join('\n');

export function createMount() {
  let W = null, mat = null;
  /** { id, mesh, x, z, yaw, taken } */
  const horses = [];
  let riding = false, ridden = -1, cd = 0, atkCd = 0;
  let horseYaw = 0, gait = 0, lean = 0;
  let stables = [];
  let promptRoot = null, promptStyle = null, promptShow = 0, inRange = false;
  const stats = { mounts: 0, dismounts: 0, attacks: 0, hits: 0, refused: 0 };

  /**
   * Build the prompt's DOM, LAZILY.
   *
   * It cannot be done in init(). This system is ORDER.MISSION (10) and the HUD
   * is ORDER.UI (100), and `initSystems` walks the systems in order — so at
   * mount's init there is no `world.ui` to hang anything on, `host` is null,
   * and the prompt silently never exists. The first version did exactly that
   * and shipped a feature with no visible surface at all; two frames of the
   * hero standing 2.6 m from a horse with nothing on screen are what found it.
   * duel.js mounts lazily for the same reason.
   */
  function mountPrompt(w) {
    if (promptRoot) return true;
    const host = w.ui && w.ui.root;
    if (!host || !host.appendChild) return false;
    promptStyle = document.createElement('style');
    promptStyle.textContent = PROMPT_CSS;
    host.appendChild(promptStyle);
    promptRoot = document.createElement('div');
    promptRoot.className = 'ride';
    promptRoot.innerHTML = '<div class="rk">Ride <b>F</b></div>';
    host.appendChild(promptRoot);
    return true;
  }

  /** Put every horse back at its stable, free and facing down the road. */
  function restage() {
    for (let i = 0; i < horses.length; i++) {
      const h = horses[i], st = stables[i];
      h.taken = false;
      if (!st) continue;
      h.x = st.x; h.z = st.z; h.yaw = st.yaw || 0;
      h.gait = 0; h.lean = 0;
    }
  }

  function spawn(id, x, z) {
    const mesh = makeHorse(mat);
    mesh.position.set(x, W.terrain ? terrainY(x, z) : 0, z);
    W.scene.add(mesh);
    const rec = { id, mesh, x, z, yaw: 0, taken: false };
    horses.push(rec);
    return rec;
  }

  function terrainY(x, z) {
    const t = W.terrain;
    if (!t) return 0;
    return t.walkHeightAt ? t.walkHeightAt(x, z) : t.heightAt(x, z);
  }

  /** The nearest free horse to the player, or null. */
  function nearest() {
    const e = W.entities, id = W.playerId;
    let best = null, bd = MOUNT.range * MOUNT.range;
    for (let i = 0; i < horses.length; i++) {
      const h = horses[i];
      if (h.taken) continue;
      const dx = h.x - e.x[id], dz = h.z - e.z[id];
      const d = dx * dx + dz * dz;
      if (d < bd) { bd = d; best = h; }
    }
    return best;
  }

  function mount(h) {
    const p = W.player;
    if (riding || !h || !p) { stats.refused++; return false; }
    riding = true; ridden = horses.indexOf(h); h.taken = true;
    horseYaw = W.entities.yaw[W.playerId];
    p.mountSpeed = MOUNT.speed;
    p.mountAccel = MOUNT.accel;
    /* `mounted` is what keeps the seated clip on screen. player.js writes the
     * hero's state from his speed every frame, so a one-shot ST.MOUNTED here
     * is overwritten before it can draw; the flag is re-read there instead.
     * Setting the state too is not redundant — it makes THIS frame right for
     * anything that reads state between here and player.js. */
    p.mounted = true;
    W.entities.state[W.playerId] = ST.MOUNTED;
    stats.mounts++;
    W.ui?.toast?.('Mounted');
    W.audio?.play?.('roll', { x: h.x, y: 1.2, z: h.z, gain: 0.7 });
    return true;
  }

  function dismount() {
    const p = W.player;
    if (!riding) return false;
    const h = horses[ridden];
    if (h) { h.taken = false; }
    riding = false; ridden = -1;
    if (p) {
      p.mountSpeed = 1; p.mountAccel = 1; p.mounted = false;
      W.entities.state[W.playerId] = ST.IDLE;
    }
    stats.dismounts++;
    W.ui?.toast?.('Dismounted');
    return true;
  }

  /**
   * One mounted sweep. Uses `world.combat.strike`, the same resolver every
   * weapon goes through, so a mounted hit is a real hit — it takes the same
   * damage path, the same hit tokens, the same VFX and the same kill events.
   */
  function attack() {
    if (!riding || atkCd > 0) return 0;
    const e = W.entities, id = W.playerId;
    atkCd = MOUNT.attackCool;
    stats.attacks++;
    /* A NEGATIVE hit token, on purpose.
     *
     * `hitToken` is how one sweep avoids reading the same body twice, and
     * combat.js hands out tokens from a counter that only ever increments
     * (`++token` in beginSwing), starting at 0. Anything this module invents
     * shares that space. The frame tick would collide only after more swings
     * than there have been frames — impossible, but only by an argument, and
     * an argument is a poor thing to leave under a silent "the blow missed".
     * The negative half of the space is unreachable from that counter, so it
     * cannot collide at all. */
    const ret = W.combat?.strike?.({
      srcId: id, team: TEAM.PLAYER, token: -((W.time ? W.time.tick : 0) | 0) - 1,
      x: e.x[id], y: e.y[id], z: e.z[id], yaw: e.yaw[id],
      half: MOUNT.arcHalf, bias: 0, r0: 0.6, r1: MOUNT.reach,
      // from the saddle down: a rider's cut lands on standing men, and the
      // band starts above a man's knee so it never clips the ground
      y0: 0.4, y1: 2.6,
      damage: MOUNT.damage, poise: 20, knock: 2.6, drag: 3.0, lift: 0.2,
      radial: 0.5, hitstop: 'light', shake: 0.34, kick: 0.3, cap: 12,
      vfx: 'slash', sfx: 'swing_heavy',
    });
    /* strike() RETURNS the body count. The first version of this diffed
     * `player.hits` across the call instead — an indirect reading of a counter
     * combat.js maintains for the player's OWN swings, which is not the same
     * question and would have reported zero for every mounted hit that landed. */
    const got = ret | 0;
    stats.hits += got;
    return got;
  }

  return {
    name: 'mount', order: ORDER.MISSION,

    init(w) {
      W = w;
      mat = surfaceMaterial({ wrap: 0.34, hemi: 1.15, spec: [26, 0.06], floor: 0x2a2118 });
      stables = stablesFrom();
      for (const st of stables) { const h = spawn(st.id, st.x, st.z); h.yaw = st.yaw; h.at = st.at; }

      w.mounts = {
        get riding() { return riding; },
        get horse() { return ridden >= 0 ? horses[ridden] : null; },
        list: horses,
        stats,
        /** Mount the nearest free horse. Returns false if none is in range. */
        mount() { return mount(nearest()); },
        /** Mount a named stable's horse wherever it is — probes and scripts. */
        mountById(id) {
          const h = horses.find((x) => x.id === id);
          if (!h) return false;
          const e = w.entities, p = w.playerId;
          e.x[p] = h.x; e.z[p] = h.z;
          return mount(h);
        },
        dismount,
        attack,
        info() {
          return {
            riding, horse: ridden >= 0 ? horses[ridden].id : null,
            horses: horses.length, free: horses.filter((h) => !h.taken).length,
            stables: horses.map((h) => ({ id: h.id, at: h.at,
              x: +h.x.toFixed(1), z: +h.z.toFixed(1), taken: h.taken })),
            speed: riding ? MOUNT.speed : 1,
            prompt: inRange, promptOpacity: +promptShow.toFixed(3),
            nearest: (() => { const h = nearest(); return h ? h.id : null; })(),
            stats: { ...stats },
          };
        },
        /** The visual pass. See createMountView() for why it is not here. */
        _view(w2) {
          for (let i = 0; i < horses.length; i++) {
            const h = horses[i];
            if (!h.mesh) continue;
            const m = h.mesh;
            /* 'YXZ', and it matters: with the default 'XYZ' the pitch is
             * applied about the WORLD x axis, so a horse heading east would
             * nod sideways. 'YXZ' turns first and then pitches about the axis
             * the turn produced, which is the horse's own. */
            if (m.rotation.order !== 'YXZ') m.rotation.order = 'YXZ';
            const g = (h.gait || 0) * Math.PI * 2;
            const on = h === horses[ridden] && riding ? 1 : 0;
            m.position.set(
              h.x,
              terrainY(h.x, h.z) + (on ? Math.abs(Math.sin(g)) * MOUNT.bob : 0),
              h.z);
            m.rotation.y = h.yaw;
            m.rotation.x = on ? Math.cos(g) * MOUNT.pitch : 0;
            m.rotation.z = on ? -(h.lean || 0) * MOUNT.roll : 0;
          }
          if (!riding) return;
          const pl = w2.chars && w2.chars.player;
          const hh = horses[ridden];
          if (!pl || !hh) return;
          /* The rider takes the horse's bob with him. Without this he hovers
           * at a fixed height while the animal rises and falls underneath —
           * which is more visible than the bob itself, because a gap opening
           * and closing at the saddle is exactly what the eye looks at. */
          pl.position.y += HORSE.saddleY + (hh.mesh ? hh.mesh.position.y - terrainY(hh.x, hh.z) : 0);
          pl.rotation.order = 'YXZ';
          pl.rotation.z = -(hh.lean || 0) * MOUNT.roll * 0.6;
        },
        /** Back to the state a fresh battle starts in. Probes lean on this. */
        reset() {
          if (riding) dismount();
          restage();
          stats.mounts = 0; stats.dismounts = 0; stats.attacks = 0;
          stats.hits = 0; stats.refused = 0;
        },
        constants: MOUNT,
      };

      /* A new battle puts the horses BACK. Freeing them without re-staging
       * leaves them wherever the last battle ended — a probe that teleports
       * one to the hero, or a ride that finished across the valley — so the
       * second battle of a session would start with no horse near the camp
       * and the stables would quietly stop meaning anything. */
      w.bus.on('mission:start', () => {
        if (riding) dismount();
        restage();
      });
      // A dead man does not stay in the saddle.
      w.bus.on('kill', (victim) => { if (victim === w.playerId && riding) dismount(); });
    },

    fixedUpdate(w, dt) {
      if (cd > 0) cd -= dt;
      if (atkCd > 0) atkCd -= dt;
      const m = w.mission;
      if (!m || m.state !== 'active' || m.paused) return;
      if (w.ui && w.ui.state !== 'playing') return;
      const inp = w.input;

      if (inp && inp.consume && inp.consume('mount', 0.3) && cd <= 0) {
        cd = MOUNT.cooldown;
        if (riding) dismount(); else mount(nearest());
      }

      if (!riding) return;
      const e = w.entities, id = w.playerId;
      const h = horses[ridden];
      if (!h) { dismount(); return; }

      /* The horse follows the rider's entity, and its HEADING lags his. That
       * lag is the only thing that makes a horse feel like a horse rather than
       * a fast man: a mount that snapped to the stick would be a speed buff
       * with a model on it. */
      const moved = Math.hypot(e.x[id] - h.x, e.z[id] - h.z);
      h.x = e.x[id]; h.z = e.z[id];
      const want = e.yaw[id];
      let d = want - horseYaw;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      const turn = d * Math.min(1, MOUNT.turnRate * dt);
      horseYaw += turn;
      h.yaw = horseYaw;

      /* The gait clock is the odometer. One full cycle per `stride` metres, so
       * the bob is locked to the ground the horse is covering and a stopped
       * horse is a still horse. */
      gait += moved / MOUNT.stride;
      if (gait > 1e6) gait -= 1e6;
      h.gait = gait;
      // ...and the lean, which is the turn rate smoothed. A horse leans INTO
      // the corner, so the sign follows the direction it is turning.
      const wantLean = dt > 0 ? Math.max(-1, Math.min(1, (turn / dt) / MOUNT.turnRate)) : 0;
      lean += (wantLean - lean) * Math.min(1, dt * 6);
      h.lean = lean;

      // a mounted attack on the ordinary attack inputs
      if (inp && inp.consume && (inp.consume('light', 0.25) || inp.consume('heavy', 0.25))) attack();
    },

    /** Fade the ride prompt. Real time, not sim time — a paused HUD still fades. */
    update(w, dt, alpha, realDt) {
      if (!promptRoot && !mountPrompt(w)) return;
      const playing = w.mission && w.mission.state === 'active' && !w.mission.paused &&
        (!w.ui || w.ui.state === 'playing');
      inRange = !!(playing && !riding && nearest());
      const k = Math.min(0.1, realDt > 0 ? realDt : 1 / 60);
      promptShow += ((inRange ? 1 : 0) - promptShow) * Math.min(1, k * 10);
      const v = promptShow < 0 ? 0 : promptShow > 1 ? 1 : promptShow;
      if (promptRoot.__o === undefined || Math.abs(promptRoot.__o - v) > 0.004) {
        promptRoot.__o = v;
        promptRoot.style.opacity = v.toFixed(3);
      }
    },

    dispose() {
      for (const h of horses) { h.mesh?.parent?.remove(h.mesh); h.mesh?.geometry?.dispose(); }
      horses.length = 0;
      mat?.dispose();
      promptRoot?.parentNode?.removeChild(promptRoot);
      promptStyle?.parentNode?.removeChild(promptStyle);
      promptRoot = null; promptStyle = null;
    },
  };
}

/**
 * The mount's VISUAL pass, registered separately and later.
 *
 * Two different ordering requirements pull in opposite directions and one
 * system cannot satisfy both:
 *
 *   - the SIM half must run BEFORE `player` (ORDER.PLAYER) so that it consumes
 *     the attack inputs first — otherwise player.js starts a ground swing on
 *     the same key press that should have been a sweep from the saddle;
 *   - the VIEW half must run AFTER `chars` (ORDER.WORLD), which sets the hero's
 *     mesh position from his entity every single frame. Applying the saddle
 *     offset any earlier is writing a value that is about to be overwritten,
 *     and the rider would stand on the ground inside his own horse.
 *
 * So: `createMount()` at ORDER.MISSION for the simulation, this at
 * ORDER.RENDER for the mesh.
 */
export function createMountView() {
  return {
    name: 'mountView', order: ORDER.RENDER,
    lateUpdate(w) { w.mounts?._view?.(w); },
  };
}
